import { Products } from "@/components/Products/Products.component";
import { useListMarketplaces, useListPartners, useListProducts } from "@/store/wallet/wallet.service";
import { useResponsive } from "@/utils/useResponsive";
import { useTranslation } from "react-i18next";
import { useBrandConfig } from "@/utils/useBrandConfig";
import { WalletStore } from "@/store/wallet/wallet.store";
import { Suspense, useEffect, useMemo } from "react";
import { Modal } from "@/components/Modals/Modal.component";
import { Logo } from "@/components/ui/Logo/Logo";
import { SettingsStore } from "@/store/settings/settings.store";

import CarouselIcon from '@/assets/svgs/carousel.svg?react';
import TableIcon from '@/assets/svgs/table.svg?react';
import { getCustomComponent } from "@/utils/custom-components.utils";

const brandsWithFAB = ['delcourtedition', 'andréeputman', 'collectionparticulière'];

const Wallet = () => {
  const { isMobile } = useResponsive();
  const { data: products } = useListProducts();
  const { digitalWalletConfig, languages, _id: brandId, name, landingPageUrl } = useBrandConfig();
  const { data: marketplaces } = useListMarketplaces(brandId);
  const { data: partners } = useListPartners(brandId);
  const setProducts = WalletStore((state) => state.setProducts);
  const setMarketplaces = WalletStore((state) => state.setMarketplaces);
  const setPartners = WalletStore((state) => state.setPartners);
  const { wasPopupSeen, setWasPopupSeen } = WalletStore();
  const { t } = useTranslation();
  const brandName = name.toLowerCase().replace(/\s+/g, '');

  const lang = SettingsStore.getState().getLanguage();
  const defaultLangage = languages[0]

  const title = digitalWalletConfig.translations?.[lang]?.title || digitalWalletConfig.translations?.[defaultLangage]?.title || t("header.WALLET");
  const modalTitle = digitalWalletConfig.translations?.[lang]?.popupTitle || digitalWalletConfig.translations?.[defaultLangage]?.popupTitle;
  const modalSubtitle = digitalWalletConfig.translations?.[lang]?.popupText || digitalWalletConfig.translations?.[defaultLangage]?.popupText;

  const isCarouselView = WalletStore((state) => state.isCarouselView);
  const setIsCarouselView = WalletStore((state) => state.setIsCarouselView);

  const isEmpty = products.length === 0;
  useEffect(() => {
    if (products) {
      setProducts(products);
    }
  }, [products, setProducts]);

  useEffect(() => {
    if (marketplaces) {
      setMarketplaces(marketplaces);
    }
  }, [marketplaces, setMarketplaces]);

  useEffect(() => {
    if (partners) {
      setPartners(partners);
    }
  }, [partners, setPartners]);

  const AddProductButton = useMemo(() => getCustomComponent({
    brandName: name,
    folder: 'AddProductButton',
  }), [name]);

  // Floating Add Product button (brand-specific direct import)

  const Header = () => (
    <div className={`flex flex-row justify-between items-center pb-4 ${isMobile ? 'px-5' : ''}`}>
      <p className={'text-headingXSmall'}>{title}</p>
      <div className="flex flex-row items-center gap-x-2">
        {isMobile && (
          isCarouselView ? (
            <button onClick={() => setIsCarouselView(false)} className="p-2 rounded-lg bg-gray-100 active:bg-gray-300 transition-colors">
              <CarouselIcon className="text-textPrimary w-5 h-5" />
            </button>
          ) : (
            <button onClick={() => setIsCarouselView(true)} className="p-2 rounded-lg bg-gray-100 active:bg-gray-300 transition-colors">
              <TableIcon className="text-textPrimary w-5 h-5" />
            </button>
          )
        )}
        <p className="text-textXSmall opacity-70">{t('wallet.products', { count: products.length })}</p>
      </div>
    </div>
  );

  const Empty = () => (
    <div className={`flex-1 flex flex-col items-center px-5 ${isMobile ? 'justify-between' : 'gap-y-11 justify-center'}`}>
      <div className={`flex flex-col items-center justify-center text-center gap-y-3 ${isMobile ? 'flex-1' : ''}`}>
        <p className="text-headingMedium">{t('wallet.welcome')}</p>
        <p className="text-bodyMedium opacity-70">{t('wallet.empty')}</p>
      </div>
    </div>
  );

  return (
    <>
      <div className={`flex flex-col flex-1 ${isMobile ? 'pt-8' : 'px-16 py-6 md:px-8 xl:px-16'}`}>
        <Header />
        {isEmpty
          ? <Empty />
          : <Products products={products} isCarouselView={isCarouselView} />
        }
        {modalTitle && <Modal
          title={modalTitle}
          subtitle={modalSubtitle}
          header={<Logo />}
          image={digitalWalletConfig.popupImage}
          open={!wasPopupSeen}
          primaryBtn={{
            label: 'START NOW',
            onClick: () => { setWasPopupSeen(true) }
          }}
        />}
      </div>
      {/* Floating action button */}
      {landingPageUrl && brandsWithFAB.includes(brandName) && (
        <div className={`${isMobile ? 'self-center mt-8' : 'fixed z-50 bottom-16 right-8'}`}>
          <Suspense>
            <AddProductButton landingPageUrl={landingPageUrl} full={false} />
          </Suspense>
        </div>
      )}
    </>
  );
};

export default Wallet;
